import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import './index.css';
import {getComponentName} from '../../common/utils/namespace';
import {createNamespace, noop} from '../../common/utils';
import {cancelRaf, raf} from '../../common/utils/raf';
import {isVNodeCreatedByComponent} from '../../common/utils/vnode';
import {addUnit} from '../../common/utils/format';
import Sticky from '../sticky';
import {getScroller, getScrollTop, setScrollTop} from '../../common/utils/dom';

var _createNamespace = createNamespace('tabs'),
    _createNamespace2 = _slicedToArray(_createNamespace, 1),
    b = _createNamespace2[0];

var scrollLeftRafId;
export default {
    name: getComponentName('Tabs'),
    data: function data() {
        return {
            position: '',
            currentIndex: null,
            customNav: false,
            children: [],
            swipe: {
                active: false,
                startX: 0
            },
            lineStyle: {
                backgroundColor: this.color
            }
        };
    },
    model: {
        prop: 'active'
    },
    props: {
        color: String,
        animated: Boolean,
        background: {
            type: String,
            default: '#fff'
        },
        lineWidth: [Number, String],
        lineHeight: [Number, String],
        lineBottom: [Number, String],
        titleActiveColor: String,
        titleInactiveColor: String,
        activeLine: {
            type: Boolean,
            default: true
        },
        active: {
            type: [Number, String],
            default: 0
        },
        hairline: {
            type: Boolean,
            default: false
        },
        ellipsis: {
            type: Boolean,
            default: true
        },
        duration: {
            type: Number,
            default: 0.3
        },
        swipeThreshold: {
            type: Number,
            default: 5
        },
        sticky: {
            type: Boolean,
            default: false
        },
        stickyContainer: {
            type: Object,
            default: null
        },
        stickyOffsetTop: {
            type: [Number, String],
            default: 0
        },
        autoScrollTop: {
            type: Boolean,
            default: false
        }
    },
    components: {
        Sticky: Sticky
    },
    computed: {
        scrollable: function scrollable() {
            return this.children.length > this.swipeThreshold || !this.ellipsis;
        },
        navStyle: function navStyle() {
            return {
                borderColor: this.color
            };
        },
        wrapStyle: function wrapStyle() {
            return {
                background: this.background
            };
        },
        currentName: function currentName() {
            var activeTab = this.children[this.currentIndex];

            if (activeTab) {
                return activeTab.computedName;
            }
        },
        trackStyle: function trackStyle() {
            var animated = this.animated,
                currentIndex = this.currentIndex,
                duration = this.duration;

            if (animated) {
                return {
                    transform: 'translate3d('.concat(-1 * currentIndex * 100, '%, 0, 0)'),
                    transitionDuration: ''.concat(duration, 's')
                };
            }
        }
    },
    methods: {
        setCurrentIndexByName: function setCurrentIndexByName(name) {
            var _a;

            var matched = this.children.filter(function(tab) {
                return tab.computedName === name;
            });
            this.setCurrentIndex((_a = matched[0]) === null || _a === void 0 ? void 0 : _a.index);
        },
        setCurrentIndex: function setCurrentIndex(currentIndex) {
            var _this = this;

            if (currentIndex !== undefined && currentIndex !== null && currentIndex !== this.currentIndex) {
                this.storeScrollTop();
                var shouldEmitChange = this.currentIndex !== null;
                this.currentIndex = currentIndex;
                this.$emit('input', this.currentName);

                if (shouldEmitChange) {
                    this.$emit('change', this.currentName, this.children[currentIndex].title);
                }

                this.$nextTick(function() {
                    _this.restoreScrollTop();
                });
            }
        },
        onClick: function onClick(index) {
            var child = this.children[index] || {},
                title = child.title,
                computedName = child.computedName;
            this.setCurrentIndex(index);
            this.$emit('click', computedName, title);
        },
        setLine: function setLine() {
            var _this2 = this;

            var currentIndex = this.currentIndex,
                lineWidth = this.lineWidth,
                lineHeight = this.lineHeight,
                lineBottom = this.lineBottom,
                duration = this.duration;
            this.$nextTick(function() {
                var titles = _this2.refTitles;

                if (!titles || !titles[currentIndex]) {
                    return;
                }

                var title = titles[currentIndex];
                var width = lineWidth ? lineWidth : title.offsetWidth / 2;
                var left = title.offsetLeft + title.offsetWidth / 2;
                var lineStyle = {
                    width: addUnit(width),
                    backgroundColor: _this2.color,
                    transform: 'translateX('.concat(left, 'px) translateX(-50%)')
                };
                lineHeight && (lineStyle['height'] = addUnit(lineHeight));
                lineBottom && (lineStyle['bottom'] = addUnit(lineBottom));
                duration && (lineStyle['transitionDuration'] = ''.concat(duration, 's'));
                _this2.lineStyle = lineStyle;
            });
        },
        storeScrollTop: function storeScrollTop() {
            if (this.currentName !== undefined && this.autoScrollTop) {
                this.scrollTopCache[this.currentName] = getScrollTop(this.scroller);
            }
        },
        restoreScrollTop: function restoreScrollTop() {
            if (this.autoScrollTop) {
                var cacheTop = this.scrollTopCache[this.currentName];

                if (cacheTop !== undefined) {
                    setScrollTop(this.scroller, cacheTop);
                }
            }
        },
        scrollIntoView: function scrollIntoView(immediate) {
            var titles = this.refTitles;

            if (!this.scrollable || !titles || !titles[this.currentIndex]) {
                return;
            }

            var nav = this.$refs.nav;
            var title = titles[this.currentIndex];
            var to = title.offsetLeft - (nav.offsetWidth - title.offsetWidth) / 2;
            this.scrollLeftTo(nav, to, immediate ? 0 : this.duration);
        },
        scrollLeftTo: function scrollLeftTo(el, to, duration) {
            cancelRaf(scrollLeftRafId);
            var from = el.scrollLeft;
            var frames = duration === 0 ? 1 : Math.round((duration * 1000) / 16);
            var count = 0;

            function animate() {
                el.scrollLeft += (to - from) / frames;

                if (++count < frames) {
                    scrollLeftRafId = raf(animate);
                }
            }

            animate();
        },
        NavWrapper: function NavWrapper() {
            var _this3 = this;

            var h = this.$createElement;
            var titleActiveColor = this.titleActiveColor,
                titleInactiveColor = this.titleInactiveColor,
                ellipsis = this.ellipsis,
                scrollable = this.scrollable,
                swipeThreshold = this.swipeThreshold,
                activeLine = this.activeLine;
            var Nav = this.children.map(function(component, index) {
                var _a, _b, _c, _d, _e;

                var title = component.title,
                    isActive = component.isActive,
                    navDisabled = component.disabled;
                var titleColor = isActive ? titleActiveColor : titleInactiveColor;
                var style = {
                    color: titleColor || undefined,
                    flexBasis: (scrollable && ellipsis && ''.concat(100 / swipeThreshold, '%')) || undefined
                };
                var navScopedSlot =
                    (_a = component === null || component === void 0 ? void 0 : component.$scopedSlots) === null ||
                    _a === void 0
                        ? void 0
                        : _a.nav;
                var navSlot =
                    (_b = component === null || component === void 0 ? void 0 : component.$slots) === null ||
                    _b === void 0
                        ? void 0
                        : _b.nav;

                var handler = function handler() {
                    if (!(component === null || component === void 0 ? void 0 : component.disabled)) {
                        _this3.onClick(index);
                    }
                };

                if (navScopedSlot || navSlot) {
                    var finalSlot =
                        typeof navScopedSlot === 'function'
                            ? navScopedSlot
                            : function() {
                                  return Array.from(navSlot);
                              };
                    var resultSlot = finalSlot({
                        active: isActive,
                        disabled: navDisabled
                    });

                    var _ref = Array.isArray(resultSlot) ? resultSlot : [resultSlot],
                        _ref2 = _slicedToArray(_ref, 1),
                        item = _ref2[0];

                    if (item) {
                        var originalClick =
                            ((_d = (_c = item.data) === null || _c === void 0 ? void 0 : _c.on) === null ||
                            _d === void 0
                                ? void 0
                                : _d.click) || noop;
                        item.data = Object.assign(
                            Object.assign({}, (item === null || item === void 0 ? void 0 : item.data) || {}),
                            {
                                ref: 'titles',
                                refInFor: 'true',
                                on: Object.assign(
                                    Object.assign(
                                        {},
                                        ((_e = item === null || item === void 0 ? void 0 : item.data) === null ||
                                        _e === void 0
                                            ? void 0
                                            : _e.on) || {}
                                    ),
                                    {
                                        click: function click() {
                                            handler();

                                            for (
                                                var _len = arguments.length, params = new Array(_len), _key = 0;
                                                _key < _len;
                                                _key++
                                            ) {
                                                params[_key] = arguments[_key];
                                            }

                                            originalClick.call.apply(originalClick, [item.context].concat(params));
                                        }
                                    }
                                )
                            }
                        );
                        item.key = 'titles-slot-'.concat(index);
                        _this3.customNav = true;
                        return item;
                    }
                }

                return h(
                    'div',
                    {
                        class: [
                            b('item', {
                                active: isActive,
                                ellipsis: ellipsis,
                                disabled: navDisabled
                            })
                        ],
                        key: 'titles-'.concat(index),
                        on: {
                            click: handler
                        },
                        ref: 'titles',
                        refInFor: 'true',
                        style: style
                    },
                    [
                        h(
                            'span',
                            {
                                class: b('text')
                            },
                            [title]
                        )
                    ]
                );
            });
            this.navNodes = Nav;
            return h(
                'div',
                {
                    class: b('nav'),
                    on: {
                        touchend: this.handleTouchend,
                        touchmove: this.handleTouchmove,
                        touchstart: this.handleTouchstart
                    },
                    ref: 'nav',
                    style: this.navStyle
                },
                [
                    Nav,
                    activeLine
                        ? h('div', {
                              class: b('line'),
                              style: this.lineStyle
                          })
                        : ''
                ]
            );
        },
        stickyWrapper: function stickyWrapper(nav) {
            var h = this.$createElement;
            var handleFixed = this.setLine;
            return this.sticky
                ? h(
                      'sticky',
                      {
                          attrs: {
                              container: this.stickyContainer,
                              'offset-top': this.stickyOffsetTop
                          },
                          on: {
                              fixed: handleFixed,
                              unfixed: handleFixed
                          }
                      },
                      [nav]
                  )
                : nav;
        },
        $titles: function $titles() {
            var $default = this.$slots.default || [];
            return $default
                .filter(function(vnode) {
                    return isVNodeCreatedByComponent(vnode, {
                        name: getComponentName('Tab')
                    });
                })
                .map(function(vnode) {
                    vnode.key = undefined;
                    return vnode;
                });
        },
        $notTitles: function $notTitles() {
            var $default = this.$slots.default || [];
            return $default.filter(function(vnode) {
                return !isVNodeCreatedByComponent(vnode, {
                    name: getComponentName('Tab')
                });
            });
        },
        handleTouchstart: function handleTouchstart(e) {
            if (this.scrollable) {
                this.swipe.startX = e.touches[0].pageX;
                this.swipe.scrollLeft = this.$refs.nav.scrollLeft;
                this.swipe.active = true;
            }
        },
        handleTouchmove: function handleTouchmove(e) {
            e.preventDefault();

            if (this.swipe.active) {
                var currentX = e.touches[0].pageX;
                var $nav = this.$refs.nav;
                var deltaX = currentX - this.swipe.startX;
                var maxLeft = $nav.scrollWidth - $nav.offsetWidth;
                var nowLeft = this.swipe.scrollLeft - deltaX;

                if (nowLeft > maxLeft || nowLeft < 0) {
                    nowLeft < 0
                        ? (this.$refs.nav.style.transform = 'translateX('.concat(-nowLeft, 'px)'))
                        : (this.$refs.nav.style.transform = 'translateX('.concat(maxLeft - nowLeft, 'px)'));
                    this.swipe.transition = true;
                } else {
                    this.$refs.nav.scrollLeft = nowLeft;
                }
            }
        },
        handleTouchend: function handleTouchend() {
            var _this4 = this;

            if (this.swipe.active && this.swipe.transition === true) {
                this.swipe.active = false;

                var handler = function handler() {
                    _this4.swipe.transition === false;
                    _this4.$refs.nav.style.transition = '';
                    _this4.$refs.nav.style.transform = '';

                    _this4.$refs.nav.removeEventListener('transitionend', handler);
                };

                this.$refs.nav.addEventListener('transitionend', handler);
                this.$refs.nav.style.transition = 'transform 0.25s ease';
                this.$refs.nav.style.transform = 'translateX(0)';
            }
        }
    },
    watch: {
        color: 'setLine',
        active: function active(name) {
            var _this5 = this;

            if (name !== this.currentName) {
                this.customNav
                    ? this.$nextTick(function() {
                          return _this5.setCurrentIndexByName(name);
                      })
                    : this.setCurrentIndexByName(name);
            }
        },
        children: function children() {
            var _this6 = this;

            this.setCurrentIndexByName(this.currentName || this.active);
            this.setLine();
            this.$nextTick(function() {
                _this6.scrollIntoView(true);
            });
        },
        currentIndex: function currentIndex() {
            this.scrollIntoView();
            this.setLine();
        }
    },
    updated: function updated() {
        var old = this.refTitles || [];
        this.refTitles = this.navNodes.map(function(item) {
            return item.elm;
        });
        this.scrollIntoView(this.refTitles.length !== old.length);
    },
    created: function created() {
        this.navNodes = [];
        this.refTitles = [];
        this.scrollTopCache = {};
    },
    mounted: function mounted() {
        this.scroller = getScroller(this.$el);
    },
    render: function render() {
        var h = arguments[0];
        var hairline = this.hairline,
            scrollable = this.scrollable,
            animated = this.animated,
            trackStyle = this.trackStyle,
            wrapStyle = this.wrapStyle;
        var $titles = this.$titles();
        var Wrap = this.stickyWrapper(
            h(
                'div',
                {
                    class: b('wrap', {
                        hairline: hairline,
                        scrollable: scrollable
                    }),
                    ref: 'wrap',
                    style: wrapStyle
                },
                [
                    this.NavWrapper($titles),
                    this.$scopedSlots.nav
                        ? this.$scopedSlots.nav({
                              currentName: this.currentName,
                              currentIndex: this.currentIndex
                          })
                        : ''
                ]
            )
        );
        return h(
            'div',
            {
                class: b()
            },
            [
                Wrap,
                this.$notTitles(),
                h(
                    'div',
                    {
                        class: b('content', {
                            animated: animated
                        })
                    },
                    [
                        animated
                            ? h(
                                  'div',
                                  {
                                      class: b('track'),
                                      style: trackStyle
                                  },
                                  [$titles]
                              )
                            : $titles
                    ]
                )
            ]
        );
    },
    install: function install(Vue) {
        Vue.component(this.name, this);
    }
};
